/**
 * The two materials of one surface that a geometry instance's fade picks between before each
 * render: the unfaded one, and the alpha-pass one with the fade alpha written into its opacity.
 * Godot holds the fade per instance and per viewport, so each render, a SubViewport's included,
 * draws the fade its own camera measures.
 */
import { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import { fadeAlpha, forcesAlphaPass } from '../../godot/rendering/fadeAlpha';
/** Null outside a GeometryInstance3D drawer, where nothing fades a surface. */
export const FadedSurfacesContext = createContext(null);
FadedSurfacesContext.displayName = 'FadedSurfacesContext';
const ATTACH_PATTERN = /^material(?:-(\d+))?$/;
/** The draw group an R3F `attach` key names: null for the mesh's one material. */
function drawGroupOf(attach) {
    const match = ATTACH_PATTERN.exec(attach ?? 'material');
    if (!match)
        throw new Error(`expected a material attach key such as material-1, got ${attach}`);
    return match[1] === undefined ? null : Number(match[1]);
}
/**
 * A material's opacity as its owner, React, sets it, with the fade alpha multiplied in place. A
 * value other than the one last written is the owner's.
 */
class FadedOpacity {
    material;
    owned;
    written = Number.NaN;
    constructor(material) {
        this.material = material;
        this.owned = material.opacity;
    }
    /** The opacity the owner last set. */
    get ownerValue() {
        if (this.material.opacity !== this.written)
            this.owned = this.material.opacity;
        return this.owned;
    }
    /** Draws the material at its owner's opacity times `alpha`. */
    scaleBy(alpha) {
        this.written = this.ownerValue * alpha;
        this.material.opacity = this.written;
    }
}
class SwappedMaterials {
    drawGroup;
    host = null;
    variants = {};
    unfadedOpacity = null;
    fade = 1;
    constructor(drawGroup) {
        this.drawGroup = drawGroup;
    }
    attach(pass, host, material) {
        this.host = host;
        this.variants[pass] = material;
        if (pass === 'unfaded')
            this.unfadedOpacity = new FadedOpacity(material);
        this.place();
        return () => {
            if (this.variants[pass] !== material)
                return;
            delete this.variants[pass];
            // The next holder of the material reads its owner's opacity.
            if (pass === 'unfaded')
                this.releaseUnfaded();
            this.place();
        };
    }
    applyFade(fade) {
        this.fade = fade;
        this.place();
    }
    releaseUnfaded() {
        this.unfadedOpacity?.scaleBy(1);
        this.unfadedOpacity = null;
    }
    /**
     * Puts the variant for the current fade on the host, at its owner's opacity times the fade
     * alpha. Godot starts ALPHA from the fade byte in every pass (`scene_forward_clustered.glsl:1251`),
     * so a fade short of a full byte lowers what a cut keeps in the surface's own pass too.
     */
    place() {
        const { host, variants, unfadedOpacity } = this;
        const { unfaded, alphaPass } = variants;
        if (!host || !unfaded || !unfadedOpacity)
            return;
        const alpha = fadeAlpha(this.fade);
        const drawn = forcesAlphaPass(this.fade) && alphaPass ? alphaPass : unfaded;
        if (drawn === alphaPass)
            alphaPass.opacity = unfadedOpacity.ownerValue * alpha;
        else
            unfadedOpacity.scaleBy(alpha);
        if (this.drawGroup === null) {
            host.material = drawn;
            return;
        }
        if (!Array.isArray(host.material))
            host.material = [];
        host.material[this.drawGroup] = drawn;
    }
}
/**
 * The function attaches of a surface's two materials, mounted at `attach`, or null outside a
 * GeometryInstance3D drawer, where the surface mounts one material at `attach`. A new `attach`
 * key gives new functions, so a material element keys on it.
 */
export function useSwappedMaterials(attach) {
    const surfaces = useContext(FadedSurfacesContext);
    const swapped = useMemo(() => (surfaces ? new SwappedMaterials(drawGroupOf(attach)) : null), [surfaces, attach]);
    useLayoutEffect(() => (swapped ? surfaces.add(swapped) : undefined), [surfaces, swapped]);
    return useMemo(() => swapped && {
        unfaded: (host, material) => swapped.attach('unfaded', host, material),
        alphaPass: (host, material) => swapped.attach('alphaPass', host, material),
    }, [swapped]);
}
//# sourceMappingURL=swappedMaterials.js.map